import { VisitInformation } from "../features/visitas/VisitaDetails/components/EditVisitInfoForm";
import { ConfirmedDeal } from "../types/empresa";
import { CreateVisitRequest, UpdateStatusRequest, VisitaInfo, VisitForCalendar } from "../types/visitas";
import { apiSlice } from "../lib/baseQuery";
import { DataFilterEndpointTags, DealsEndpointTags, VisitasEndpointTags } from "./utils";
import { handleFileDownload } from "src/utils/handleFileDowload";

const BASE_URL = "/api/visitas";

export const visitasApi = apiSlice.injectEndpoints({
    endpoints: (build) => ({
        getVisitaById: build.query<VisitaInfo, { visitaId: number, rawFormat?: boolean }>({
            query: (request) => {
                const rawFormat = request.rawFormat ? "true" : "false";
                const queryString = new URLSearchParams({ rawFormat });
                return BASE_URL + `/${request.visitaId}?${queryString}`;
            },
            providesTags: [VisitasEndpointTags.GET_VISITA_BY_ID]
        }),
        updateStatus: build.mutation<void, UpdateStatusRequest>({
            query: (request) => ({
                url: BASE_URL + `/${request.visitaId}/update-status`,
                method: "PUT",
                body: {
                    status: request.status,
                    intenciones: request.intenciones,
                },
            }),
            invalidatesTags: [
                VisitasEndpointTags.GET_VISITA_BY_ID,
                DealsEndpointTags.GET_DEAL_INFO_BY_ID,
            ],
        }),
        addVendedores: build.mutation<void, { visitaId: number, vendedores: number[]}>({
            query: (request) => ({
                url: BASE_URL + `/${request.visitaId}/add-vendedores`,
                method: "POST",
                body: {
                    vendedores: request.vendedores,
                },
            })
        }),
        deleteVendedorFromVisita: build.mutation<void, { visitaId: number, vendedorId: number }>({
            query: (request) => ({
                url: BASE_URL + `/${request.visitaId}/delete-vendedor/${request.vendedorId}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                DataFilterEndpointTags.GET_USERS_FOR_FILTER,
            ]
        }),
        getVisits: build.query<ConfirmedDeal[], { fecha: string, formatResponse: "calendar" | "list" }>({
            query: (request) => {
                const queryString = new URLSearchParams(request);
                return BASE_URL + `?${queryString}`;
            }
        }),
        getVisitsForCalendar: build.query<VisitForCalendar[], { year: number, month: number }>({
            query: (request) => {
                const queryString = new URLSearchParams({
                    year: request.year.toString(),
                    month: request.month.toString(),
                });
                return BASE_URL + `/calendar-format?${queryString}`;
            }
        }),
        updateVisitInformation: build.mutation<void, { visitaId: number, data: VisitInformation}>({
            query: (request) => ({
                url: BASE_URL + `/${request.visitaId}`,
                method: "PUT",
                body: request.data,
            })
        }),
        createVisit: build.mutation<void, CreateVisitRequest>({
            query: (request) => ({
                url: BASE_URL,
                method: "POST",
                body: request,
            }),
            invalidatesTags: (result, error, arg, meta) => error ? [] : [
                DealsEndpointTags.GET_DEAL_INFO_BY_ID
            ],
        }),
        exportVisits: build.query<void, { fechaInicial: string, fechaFinal: string, connId: string }>({
            query: (request) => {
                const queryString = new URLSearchParams(request);
                return {
                    url: BASE_URL + `/export?${queryString}`,
                    method: "GET",
                };
            },
            keepUnusedDataFor: 0,
        }),
        downloadExportVisits: build.query<void, string>({
            query: (jobId) => ({
                url: BASE_URL + `/downloads/${jobId}.xlsx`,
                method: "GET",
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const contentDisposition = response.headers.get("content-disposition");
                    const fileName = contentDisposition?.split(";")[1]?.split("filename=")[1]?.trim()
                    return {
                        blob,
                        fileName,
                        mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    };
                },
            }),
            transformResponse: ({ blob, fileName, mimeType }: {
                blob: Blob;
                fileName: string;
                mimeType: string;
            }) => handleFileDownload(blob, fileName, mimeType),
            keepUnusedDataFor: 0,
        })
    }),
});

export const {
    useGetVisitaByIdQuery,
    useUpdateStatusMutation,
    useAddVendedoresMutation,
    useDeleteVendedorFromVisitaMutation,
    useGetVisitsQuery,
    useGetVisitsForCalendarQuery,
    useUpdateVisitInformationMutation,
    useCreateVisitMutation,
    useLazyExportVisitsQuery,
    useLazyDownloadExportVisitsQuery,
} = visitasApi;
